import { getDestinationCountryRecommendation } from "~/sassmall/api/internationalization";
import { findAvailableCurrency } from "~/sassmall/currencyPreference";

const parseObject = (value) => {
  try {
    const result = typeof value === "string" ? JSON.parse(value) : value;
    return result && typeof result === "object" && !Array.isArray(result) ? result : {};
  } catch { return {}; }
};

export function getIpPreferenceConfig(shop = {}) {
  const follow = parseObject(shop.offiaccountFollowData);
  const records = [parseObject(follow.otherJson), parseObject(parseObject(shop.offiaccountFollowUrl).otherJson),
    parseObject(shop.storePageInfo?.otherJson), parseObject(shop.otherJson)];
  const value = (key) => {
    for (const record of records) {
      const found = record[key] ?? parseObject(record.reactAppConfig)[key];
      if (found != null) return found;
    }
  };
  const enabled = value("autoSelectLocaleCurrencyByIp");
  return {
    enabled: enabled == null || enabled === true || enabled === 1 || enabled === "1" || enabled === "true",
    apiUrl: String(value("ipLocaleApiUrl") || "https://api.ip.sb/geoip/").trim(),
  };
}

export function normalizePreferenceLocale(value, store) {
  const locale = store.normalizeMallSiteLocale(value);
  if (["zh-CN", "zh-TW", "en", "ja_JP", "vi_VN"].includes(locale)) return locale;
  const normalized = String(value || "").trim().toLowerCase().replace(/_/g, "-");
  if (/^zh-(tw|hk|mo|hant)/.test(normalized)) return "zh-TW";
  if (/^zh/.test(normalized)) return "zh-CN";
  if (/^en/.test(normalized)) return "en";
  if (/^ja/.test(normalized)) return "ja_JP";
  if (/^vi/.test(normalized)) return "vi_VN";
  return "";
}

export function getCurrentDisplayPreference(store, locale) {
  const shop = store.storeInfoSourceDataMap[store.activeStoreServer] || {};
  const savedLocale = typeof window === "undefined" ? "" : window.localStorage.getItem("i18n_current_locale");
  const language = normalizePreferenceLocale(locale || savedLocale || shop.defaultLanguage, store);
  return {
    effectiveLanguage: language === "en" ? "en-US" : language === "ja_JP" ? "ja-JP" : language === "vi_VN" ? "vi-VN" : language || "en-US",
    effectiveCurrency: String(store.currentCurrency?.toCurrencyCode || shop.defaultCurrencyCode || "USD").toUpperCase(),
  };
}

export async function resolveInitialPreference(store, locale, hadLocalCurrency = true) {
  const shop = store.storeInfoSourceDataMap[store.activeStoreServer] || {};
  const config = getIpPreferenceConfig(shop);
  const savedLocale = normalizePreferenceLocale(window.localStorage.getItem("i18n_current_locale"), store);
  const deviceLocale = savedLocale ? "" : normalizePreferenceLocale(navigator.languages?.[0] || navigator.language, store);
  const savedCurrency = store.initCurrentCurrency();
  const findInitialCurrency = (value) => {
    const code = String(value || "").trim().toUpperCase();
    if (!code) return null;
    const listed = findAvailableCurrency(store, code);
    if (listed) return listed;
    if (store.currencyList.length) return null;
    return {
      id: `fallback-${code}`, fromCurrencyCode: "CNY", toCurrencyCode: code,
      exchangeRate: code === "CNY" ? 1 : 0, activeFlag: true, deleteFlag: false,
    };
  };
  const validCurrency = hadLocalCurrency && findInitialCurrency(savedCurrency?.toCurrencyCode);
  let recommendation = null;
  if (config.enabled && !validCurrency) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      let country;
      try {
        const response = await fetch(config.apiUrl, { headers: { accept: "application/json" }, signal: controller.signal });
        if (!response.ok) throw new Error(`IP country request failed: ${response.status}`);
        const body = await response.json();
        country = String(body.country_code || body.countryCode || body.country_code2 || body.data?.country_code || "").toUpperCase();
      } finally { clearTimeout(timer); }
      if (/^[A-Z]{2}$/.test(country)) recommendation = await getDestinationCountryRecommendation(country);
    } catch (error) { console.warn("目的国推荐配置加载失败:", error); }
  }
  const nextLocale = savedLocale || deviceLocale || store.getMallSiteDefaultLocale() || "en";
  const recommendedCurrency = recommendation?.matched && findInitialCurrency(recommendation.defaultCurrencyCode);
  const shopCurrency = findInitialCurrency(store.getMallSiteDefaultCurrency() || shop.defaultCurrencyCode);
  const nextCurrency = validCurrency || recommendedCurrency || shopCurrency ||
    findInitialCurrency("USD") || {
      id: "fallback-USD", fromCurrencyCode: "CNY", toCurrencyCode: "USD",
      exchangeRate: 0, activeFlag: true, deleteFlag: false,
    };
  if (!nextCurrency) throw new Error("没有可用的展示币种");
  if (!validCurrency || !store.currencyList.length) {
    store.saveCurrentCurrency(nextCurrency);
    if (!validCurrency) window.localStorage.setItem("currentCurrencySource", "AUTO");
  }
  if (locale.value !== nextLocale) await locale.set(nextLocale);
  return { locale: nextLocale, currency: nextCurrency };
}
